import {
  DialogAction,
  DialogActions,
  DialogClose,
  DialogContent,
} from "components/Dialog";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, ButtonColor, ButtonSize } from "components/Button";
import {
  ApiError,
  OrgSubjectsService,
  OrganizationSubjectAdminResponse,
  TutorLimitedResponse,
  TutorSubjectsService,
  TutorSubjectAdminResponse,
} from "client/openapi";
import { APIResponse } from "types";
import Notifications from "util/notifications";
import CurrencyInput, { CurrencyInputProps } from "react-currency-input-field";
import { Select } from "components/Select";
import "./index.css";

type PricesMap = Record<number, number>; // orgSubjectId -> custom rate in cents
type Option = { value: number | string; label: string } | null;

export default function CustomRatesModal({
  orgId,
  tutor,
}: {
  orgId: number;
  tutor: TutorLimitedResponse;
}) {
  // Data
  const [subjects, setSubjects] = useState<OrganizationSubjectAdminResponse[]>(
    []
  );
  // Only keep subjects that already have a custom rate
  const [currentTutorPrices, setCurrentTutorPrices] = useState<PricesMap>({});

  // UI / status
  const [error, setError] = useState<APIResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Per-row editing state
  const [editingIds, setEditingIds] = useState<Set<number>>(new Set());
  const [pendingNewIds, setPendingNewIds] = useState<Set<number>>(new Set());
  const [editValues, setEditValues] = useState<
    Record<number, number | undefined>
  >({});
  const [invalidPrices, setInvalidPrices] = useState<Record<number, boolean>>(
    {}
  );
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());

  // "Add subject" select value (we clear it right after each selection)
  const [addSelectValue, setAddSelectValue] = useState<Option>(null);

  // -------- Helpers
  const formatUSD = (cents?: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number(cents || 0) / 100.0);

  const addEditingId = (id: number) =>
    setEditingIds((prev) => new Set(prev).add(id));
  const removeEditingId = (id: number) =>
    setEditingIds((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });

  const addPendingNewId = (id: number) =>
    setPendingNewIds((prev) => new Set(prev).add(id));
  const removePendingNewId = (id: number) =>
    setPendingNewIds((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });

  const addSavingId = (id: number) =>
    setSavingIds((prev) => new Set(prev).add(id));
  const removeSavingId = (id: number) =>
    setSavingIds((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });

  const clearPerRowState = (id: number) => {
    setEditValues((prev) => {
      const { [id]: _drop, ...rest } = prev;
      return rest;
    });
    setInvalidPrices((prev) => {
      const { [id]: _drop, ...rest } = prev;
      return rest;
    });
  };

  // -------- API calls
  const getExistingPricesForTutor = useCallback(async () => {
    try {
      const tutorSubjects = await TutorSubjectsService.getSubjectsByTutor({
        tutorId: tutor.id,
      });
      const prices: PricesMap = {};
      (tutorSubjects as TutorSubjectAdminResponse[]).forEach((subject) => {
        if (
          subject.custom_pay_rate !== null &&
          subject.custom_pay_rate !== undefined
        ) {
          prices[subject.org_subject.id] = subject.custom_pay_rate as number;
        }
      });
      setCurrentTutorPrices(prices);
    } catch (e: any) {
      setError({ message: "Unable to load tutor subjects." });
      console.error(`Error (#${e.status}): ${e.message}`);
      throw e; // Re-throw to be caught by Promise.all
    }
  }, [tutor.id]);

  const getAndSetSubjects = useCallback(async () => {
    try {
      const subs = await OrgSubjectsService.getSubjectsByOrg({ orgId });
      setSubjects(subs as OrganizationSubjectAdminResponse[]);
    } catch (e: any) {
      setError({ message: "Unable to load org subjects." });
      console.error(`Error (#${e.status}): ${e.message}`);
      throw e; // Re-throw to be caught by Promise.all
    }
  }, [orgId]);

  // Combined loading function
  const loadInitialData = useCallback(async () => {
    setIsLoading(true);

    try {
      await Promise.all([getExistingPricesForTutor(), getAndSetSubjects()]);
    } catch (_error) {
      // Error handling is done in individual functions
    } finally {
      setIsLoading(false);
    }
  }, [getExistingPricesForTutor, getAndSetSubjects]);

  // -------- Price validation & input handling (per row)
  const verifyPricingEdit: CurrencyInputProps["onValueChange"] = (
    value,
    name
  ) => {
    const id = Number(name);
    if (!name || Number.isNaN(id)) return;

    // Empty => user hasn't typed yet
    if (value === undefined || value === null || value === "") {
      setInvalidPrices((prev) => ({ ...prev, [id]: false }));
      setEditValues((prev) => ({ ...prev, [id]: undefined }));
      return;
    }

    const num = Number(value);
    if (Number.isNaN(num)) {
      setInvalidPrices((prev) => ({ ...prev, [id]: true }));
      setError({ message: "Please enter a valid number." });
      return;
    }

    if (num > 5000) {
      setInvalidPrices((prev) => ({ ...prev, [id]: true }));
      setError({
        message: "Please enter a value less than or equal to $5000.00",
      });
      return;
    }

    if (num < 0) {
      setInvalidPrices((prev) => ({ ...prev, [id]: true }));
      setError({
        message: "Please enter a value greater than or equal to $0.00",
      });
      return;
    }

    setInvalidPrices((prev) => ({ ...prev, [id]: false }));
    setEditValues((prev) => ({ ...prev, [id]: Math.round(num * 100) })); // cents
  };

  async function saveRate(id: number) {
    const isNew = pendingNewIds.has(id);
    const invalid = !!invalidPrices[id];

    // For new rows, the user MUST type a value before Save
    const centsToSave = isNew
      ? editValues[id]
      : editValues[id] ?? currentTutorPrices[id];

    if (isNew && (centsToSave === undefined || Number.isNaN(centsToSave))) {
      setError({ message: "Tutor pay is a required field." });
      return;
    }
    if (invalid) {
      setError({ message: "Please fix the invalid pay before saving." });
      return;
    }

    addSavingId(id);
    try {
      const edited = await TutorSubjectsService.setCustomSubjectPayRateForTutor(
        {
          tutorId: tutor.id,
          orgSubjectId: id,
          customPayRate: centsToSave as number,
        }
      );

      setCurrentTutorPrices((prev) => ({
        ...prev,
        [id]: edited.custom_pay_rate as number,
      }));

      removePendingNewId(id);
      removeEditingId(id);
      clearPerRowState(id);

      Notifications.success("Custom pay saved!");
    } catch (e: any) {
      const apiErr = e as ApiError;
      setError({ message: "Unable to save tutor pay." });
      console.error(`Error (#${apiErr?.status}): ${apiErr?.message}`);
    } finally {
      removeSavingId(id);
    }
  }

  async function deleteRate(id: number) {
    // Only invoked when NOT in edit mode
    addSavingId(id);
    try {
      await TutorSubjectsService.setCustomSubjectPayRateForTutor({
        tutorId: tutor.id,
        orgSubjectId: id,
        // Delete = set to null (per requirements)
        customPayRate: null as unknown as number,
      });

      setCurrentTutorPrices((prev) => {
        const { [id]: _drop, ...rest } = prev;
        return rest;
      });

      removeEditingId(id);
      removePendingNewId(id);
      clearPerRowState(id);

      Notifications.success("Custom pay deleted.");
    } catch (e: any) {
      const apiErr = e as ApiError;
      setError({ message: "Unable to delete tutor pay." });
      console.error(`Error (#${apiErr?.status}): ${apiErr?.message}`);
    } finally {
      removeSavingId(id);
    }
  }

  function cancelEdit(id: number) {
    // If it was a brand-new, unsaved row -> remove it
    if (pendingNewIds.has(id) && currentTutorPrices[id] === undefined) {
      removePendingNewId(id);
    }
    removeEditingId(id);
    clearPerRowState(id);
  }

  function handleClose() {
    // reset local UI state
    setEditingIds(new Set());
    setPendingNewIds(new Set());
    setEditValues({});
    setInvalidPrices({});
    setSavingIds(new Set());
  }

  // -------- Effects
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (error) {
      Notifications.error(error?.message || "An unexpected error occurred.");
    }
  }, [error]);

  // -------- Derived UI lists
  const existingCustomIds = useMemo(
    () =>
      Object.keys(currentTutorPrices)
        .map((k) => Number(k))
        .filter(
          (id) =>
            currentTutorPrices[id] !== null &&
            currentTutorPrices[id] !== undefined
        ),
    [currentTutorPrices]
  );

  // Rows to render: existing custom-rate subjects + any pending new selections
  const rowSubjectIds = useMemo(() => {
    const pendingIds = Array.from(pendingNewIds);
    return Array.from(new Set([...existingCustomIds, ...pendingIds]));
  }, [existingCustomIds, pendingNewIds]);

  const rows = useMemo(
    () =>
      rowSubjectIds
        .map((id) => subjects.find((s) => s.id === id))
        .filter(Boolean)
        .sort((a, b) =>
          a!.name > b!.name ? 1 : a!.name < b!.name ? -1 : 0
        ) as OrganizationSubjectAdminResponse[],
    [rowSubjectIds, subjects]
  );

  // Subjects available to add (not already in the table)
  const addableSubjects = useMemo(
    () =>
      subjects
        .filter(
          (s) => !existingCustomIds.includes(s.id) && !pendingNewIds.has(s.id)
        )
        .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0)),
    [subjects, existingCustomIds, pendingNewIds]
  );

  const addOptions: { value: number; label: string }[] = useMemo(
    () =>
      addableSubjects.map((s) => ({
        value: s.id,
        label: `${s.name} — default ${formatUSD(
          Number(s.default_pay_for_tutors || 0)
        )}`,
      })),
    [addableSubjects]
  );

  // -------- Render
  return (
    <DialogContent className="custom-tutor-price-dialog" onClose={handleClose}>
      <div className="mt-3 font-semibold text-center header text-lg">
        Set Custom Rates
      </div>

      {isLoading ? (
        <div className="mt-4 text-center">
          <div className="inline-block">
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
            <span className="ml-2 text-sm text-gray-600">Loading data...</span>
          </div>
        </div>
      ) : rows.length > 0 ? (
        <table className="table mt-3">
          <thead className="text-left text-xs text-gray-500">
            <tr>
              <th className="w-3/12">Subject/Activity</th>
              <th className="w-3/12">Default Rate (per hour)</th>
              <th className="w-3/12">{tutor.first_name} Receives (per hour)</th>
              <th className="w-3/12">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => {
              const id = s.id;
              const isEditing = editingIds.has(id);
              const isSaving = savingIds.has(id);
              const isNew = pendingNewIds.has(id);
              const hasInvalid = !!invalidPrices[id];
              const currentCustom = currentTutorPrices[id]; // cents
              const defaultCents = Number(s.default_pay_for_tutors || 0);

              return (
                <tr
                  key={id}
                  className="tutor-subject-item odd:bg-gray-50 even:bg-white"
                >
                  <td>
                    <div title={s.name} className="tutor-subject-title">
                      {s.name}
                    </div>
                  </td>

                  {/* Default org pay for tutors */}
                  <td>{formatUSD(defaultCents)}</td>

                  {/* Custom pay (edit or read-only) */}
                  <td>
                    {isEditing ? (
                      <CurrencyInput
                        className="input w-32 h-8"
                        id={`custom_tutor_rate_${id}`}
                        name={String(id)}
                        placeholder={isNew ? "" : undefined}
                        defaultValue={
                          isNew ? undefined : (currentCustom / 100)?.toFixed(2)
                        }
                        prefix="$"
                        decimalsLimit={2}
                        allowNegativeValue={false}
                        onValueChange={verifyPricingEdit}
                      />
                    ) : (
                      formatUSD(currentCustom)
                    )}
                  </td>

                  {/* Actions */}
                  <td>
                    <div
                      className="mb-2"
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      {isEditing ? (
                        <>
                          <Button
                            size={ButtonSize.EXTRA_SMALL}
                            color={
                              hasInvalid ||
                              (isNew && editValues[id] === undefined)
                                ? ButtonColor.GRAY
                                : ButtonColor.GREEN
                            }
                            disabled={
                              isSaving ||
                              hasInvalid ||
                              (isNew && editValues[id] === undefined)
                            }
                            onClick={() => saveRate(id)}
                          >
                            {isSaving ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            ) : (
                              <>Save</>
                            )}
                          </Button>

                          {/* EDIT MODE: show Cancel (not Delete) */}
                          <Button
                            size={ButtonSize.EXTRA_SMALL}
                            color={ButtonColor.GRAY}
                            disabled={isSaving}
                            onClick={() => cancelEdit(id)}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size={ButtonSize.EXTRA_SMALL}
                            color={ButtonColor.PURPLE}
                            onClick={() => {
                              addEditingId(id);
                            }}
                          >
                            Edit
                          </Button>

                          {/* NON-EDIT MODE: show Delete */}
                          <Button
                            size={ButtonSize.EXTRA_SMALL}
                            color={ButtonColor.GRAY}
                            disabled={isSaving}
                            onClick={() => deleteRate(id)}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className="mt-4 text-sm text-gray-600 text-center">
          No custom rates yet.
        </div>
      )}

      {/* Add-new control — uses components/Select */}
      {!isLoading && subjects.length > 0 && (
        <div className="mt-4">
          <label className="block text-xs text-gray-600 mb-2 italic">
            Add a custom rate
          </label>
          <Select
            className="mt-2 w-full md:w-1/2"
            value={addSelectValue}
            options={addOptions}
            placeholder="-- Select a subject --"
            onChange={(option: Option) => {
              if (!option) {
                setAddSelectValue(null);
                return;
              }
              const val = Number(option.value);
              addPendingNewId(val);
              addEditingId(val);
              // New row: start with empty custom pay => must type to enable Save
              setEditValues((prev) => ({ ...prev, [val]: undefined }));
              // Clear the select so user can add another immediately
              setAddSelectValue(null);
            }}
            isClearable
          />
          {addOptions.length === 0 && (
            <div className="mt-2 text-xs text-gray-500">
              No additional subjects available to customize.
            </div>
          )}
        </div>
      )}

      <DialogActions>
        <DialogClose asChild>
          <DialogAction color={ButtonColor.PURPLE} onClick={handleClose}>
            Close
          </DialogAction>
        </DialogClose>
      </DialogActions>
    </DialogContent>
  );
}
